import React from 'react';
import {useDropzone} from 'react-dropzone';
import classnames from 'classnames';
import * as styles from './FileUpload.module.scss';
import { Link } from 'gatsby';
import Axios from 'axios';
import FileIconIcon from '../../assets/file.icon.svg';
import FileIconAdd from '../../assets/file.add.icon.svg';
import FileIconDone from '../../assets/file.done.icon.svg';
import FileIconUploading from '../../assets/file.uploading.icon.svg';
import DeleteIcon from '../../assets/delete.icon.svg';
import Portal from '../Portal';
import {isEqual, cloneDeep} from 'lodash';

export type IUploadedFiles = {[key: string]: { filename: string; serverFilename: string;  uploadProgress: number; isNew: boolean; uploaded: boolean; expires: string; file: File}}

type Props = {
    onChange: (data: IUploadedFiles) => void;
    value: IUploadedFiles;
}

const allowedDataTypes: {
    [key: string]: {
        name: string;
        extension: string;
    }
} = {
    "image/svg+xml": { name: "SVG", extension: ".svg"},
    "application/pdf": { name: "PDF", extension: ".pdf"},
    "image/jpeg": { name: "JPG", extension: ".jpg"},
    "image/heic": { name: "Heic", extension: ".heic"},
    "image/png": { name: "PNG", extension: ".png"},
    "application/postscript": { name: "EPS", extension: ".eps"},
}


const FileIcon = (props: {type?: "add" | "uploading" | "done"}) => {
    const {type} = props;

    return <svg width="100%" height="100%" viewBox="0 0 22 30" version="1.1" 
        xmlns="http://www.w3.org/2000/svg" >
            <FileIconIcon />
            {type === "add" && <FileIconAdd />}
            {type === "uploading" && <FileIconUploading />}
            {type === "done" && <FileIconDone />}
            
    </svg>
}

export default function FileUpload (props: Props) {
    const {value} = props;
    const uploadedFilesFromProps = (value) ? value : {};
    const [uploadedFiles, setUploadedFiles]= React.useState<IUploadedFiles>(uploadedFilesFromProps);
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const reset = React.useRef(false);

    const onDrop = async acceptedFiles => {
        for (const file of acceptedFiles) {
            // Check filetype
            if (!allowedDataTypes.hasOwnProperty(file.type)) {
                setErrorMessageModal(<>Dieser Dateityp wird nicht unterstützt! Folgende Dateien können hochgeladen werden: <span className="has-text-weight-bold">{Object.keys(allowedDataTypes).map(type => allowedDataTypes[type].extension + " ")}</span></>)
                continue;
            }

            // Check if filename already exists
            if (uploadedFiles.hasOwnProperty(file.name)) {
                setErrorMessageModal(<>Diese Datei wurde bereits hochgeladen</>);
                continue;
            }

            setModalOpen(false);

            // Upload file
            setUploadedFiles((uploaded) => { 
                const newFileObject: IUploadedFiles = cloneDeep(uploaded);
                newFileObject[file.name] = {
                    filename: file.name,
                    serverFilename: null,
                    uploadProgress: 0,
                    uploaded: false,
                    isNew: true,
                    expires: "",
                    file: file,
                }
                reset.current = false;
                return newFileObject;
            });

            // Upload progress
            const config = {
                onUploadProgress: async function(progressEvent) {
                    var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    setUploadedFiles((uploaded) => { 
                        const newFileObject: IUploadedFiles = cloneDeep(uploaded);
                        if (newFileObject[file.name]) {
                            newFileObject[file.name].uploadProgress = percentCompleted / 100;
                        }
                        reset.current = false;
                        return newFileObject;
                    });
                }
            }
            
            // Create form data to send file via native js
            let data = new FormData();
            data.append('file', file);
            
            try {
                const result = await Axios.post(process.env.API_URL + 'files', data, config);

                setUploadedFiles((uploaded) => {
                    const newFileObject: IUploadedFiles = cloneDeep(uploaded);
                    newFileObject[file.name].serverFilename = result.data.filename;
                    newFileObject[file.name].uploaded = true;

                    reset.current = false;
                    return newFileObject;
                });
            } catch (e) {
                setErrorMessage("Das Hochladen hat leider nicht geklappt. " + e);
                // Remove File
                setUploadedFiles((uploaded) => { 
                    const newFileObject: IUploadedFiles = cloneDeep(uploaded);
                    if (newFileObject[file.name]) {
                        delete newFileObject[file.name];
                    }
                    reset.current = false;
                    return newFileObject;
                });
            }
              

        }
    };



    React.useEffect(() => {
        if (value === null) {
            reset.current = true;
            setUploadedFiles({});
            onChange({});
        }
    }, [value]);


    // THIS HERE PROPABLY CAUSES THE ERROR:::!!!!!!!!!!!!!!!!!!#############
    // The is uploaded should be triggerd after await and not in progess function
    // Call callback only if uploading of each file is complete
    React.useEffect(() => {
        if (reset.current === true) {
            return;
        }
        let doneUploading: boolean = true;
        for (const filename in uploadedFiles) {
            if (!uploadedFiles[filename].uploaded) {
                doneUploading = false;
            }
        }

        if (doneUploading && Object.keys(uploadedFiles).length > 0 && !isEqual(uploadedFiles, uploadedFilesFromProps)) {
            setTimeout(() => {
                onChange(uploadedFiles);
            }, 100);
        }
    }, [uploadedFiles, uploadedFilesFromProps]);


    // Reset isNew after highlight Animation played
    // React.useEffect(() => {
    //     let hasIsNews: boolean = false;
    //     for (const filename in uploadedFiles) {
    //         if (uploadedFiles[filename].isNew) {
    //             hasIsNews = true;
    //         }
    //     }

    //     if (hasIsNews) {
    //         const timer = setTimeout(() => {
    //             setUploadedFiles((uploaded) => { 
    //                 const newFileObject: IUploadedFiles = cloneDeep(uploaded);
    //                 for (const filename in newFileObject) {
    //                     // newFileObject[filename].isNew = false;
    //                 }
    //                 setTimeout(() => {
    //                     // onChange(newFileObject);
    //                 }, 100);
    //                 return newFileObject;
    //             });
    //         }, 3000); // duratio of animation because animation will be stopped after rerendering

    //         return () => clearTimeout(timer);
    //     }
    // }, [uploadedFiles])
    
    const {acceptedFiles, getRootProps, getInputProps, isDragActive} = useDropzone({onDrop});
    const [agreed, setAgreed] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [errorMessageModal, setErrorMessageModal] = React.useState(null);
    const inputField = React.useRef(null);

    const accept = () => {
        setAgreed(true);
    }

    const prevent = (e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
    }

    const closeErrorMessage = () => {
        setErrorMessage(null);
    }

    const closeErrorMessageModal = () => {
        setErrorMessageModal(null);
    }

    const deleteFile = (filename: string) => {
        setUploadedFiles((uploaded) => { 
            const newFileObject: IUploadedFiles = cloneDeep(uploaded);
            delete newFileObject[filename];
            reset.current = false;
            onChange(newFileObject);
            return newFileObject;
        });
    }

    const onChange = (data: any) => {
        let dataToSend = data;
        Object.keys(data).forEach((key) => {
            delete dataToSend[key].isNew;
        });

        props.onChange(dataToSend);
    }

    return <div>

            <div className={classnames([styles.wrapper])}>

                {errorMessage && <div className="notification is-danger">
                    <button className="delete" onClick={closeErrorMessage}></button>
                    {errorMessage}
                </div>}
                
                <ul className={styles.filesList}>
                        {Object.keys(uploadedFiles).map(filename => {
                            const file = uploadedFiles[filename];
                            
                            return <li key={file.filename}  className={classnames([styles.filesItem, file.isNew && file.uploaded ? "highlighting" : ""])}>

                                {file.uploadProgress < 1 && <i className={classnames([ styles.icon])} ><FileIcon type="uploading" /></i>} 
                                {file.uploadProgress >= 1 && <i className={classnames([ styles.icon])} ><FileIcon type="done" /></i>} 
                                <span className={styles.filesItemName}>{file.filename}</span>
                                <i className={classnames([styles.icon, styles.deleteIcon])} onClick={() => { deleteFile(file.filename) }}><DeleteIcon /></i>


                                {file.uploadProgress < 1 && <i className={styles.uploadProgress} style={{width: (file.uploadProgress * 100) + "%"}}></i>}
                            </li>
                        })}
                        <li className={classnames([styles.filesItem, styles.addFile])} onClick={(e) => { setModalOpen(true); }}>
                            <i className={classnames([ styles.icon])} ><FileIcon type="add" /></i>
                            Druckdatei hinzufügen
                        </li>
                </ul>
            </div>

            
    <Portal>
        <div className={classnames(["modal", modalOpen ? "is-active" : "", styles.modal])}>
        <div className={classnames(["modal-background", styles.modalBackground])} onClick={() => { setModalOpen(false); }}></div>
            <div className={classnames(["modal-content", styles.modalContent])}>
                <div className={classnames([styles.modal43])}>
                    <div className={classnames([styles.modal43container])}>

                    <button className={classnames(["delete", styles.closeButton])} aria-label="close" onClick={() => { setModalOpen(false); }}></button>

               

                        {errorMessageModal && <div className="notification is-danger">
                            <button className="delete" onClick={closeErrorMessageModal}></button>
                            {errorMessageModal}
                        </div>}
                    
                        
                            {!agreed && <div className={styles.modal169}>
                                <div className={styles.dropZone}>
                                    <div className={styles.gdprDialog}>
                                        Die hochgeladenen Daten werden nur zum Zweck der Bearbeitung Ihres Anliegens verarbeitet. Weitere Informationen finden Sie in unserer <Link to="/datenschutz">Datenschutzerklärung</Link>.<br /><br />
                                        <span className="button is-primary is-rounded" onClick={accept}>Einverstanden</span>
                                    </div>
                                </div>
                            </div>}
                        
                            {agreed && <>
                            <div className={styles.modal169}>
                                    <div {...getRootProps({className: ""})}>
                                        <input {...getInputProps({className: "file-input"})} ref={inputField} name="files" onClick={prevent} />
                                        <div className={classnames(["file-cta", styles.dropZone, isDragActive ? styles.dragActive : ""])} onClick={() => { inputField.current.click(); }}>
                                                <div className={styles.fieldLabelSpan}>
                                                    <svg width="30%" viewBox="0 0 35 28" version="1.1" xmlns="http://www.w3.org/2000/svg" style={{fillRule: "evenodd", clipRule: "evenodd", strokeLinecap: "round", strokeLinejoin: "round", strokeMiterlimit:10}}>
                                                        <path d="M22.5,15.094l-6.027,-6.146l-6.092,6.146" style={{fill: "none", fillRule: "nonzero", stroke: "#009ee0", strokeWidth: "0.75px" }}/>
                                                        <path d="M16.707,14.197l0,12.615" style={{fill: "none", fillRule: "nonzero", stroke: "#009ee0", strokeWidth: "0.75px" }}/>
                                                        <path d="M0.375,17.25c1.715,-11.25 7.34,-16.875 16.875,-16.875c9.535,0 15.16,5.625 16.875,16.875" style={{fill: "none", fillRule: "nonzero", stroke: "#009ee0", strokeWidth: "0.75px" }}/>
                                                    </svg>
                                                </div>
                                                

                                                <div className={classnames(["has-text-weight-bold", styles.fieldLabelSpan])}>Dateien hineinziehen</div>
                                        </div>
                                
                                </div>
                            </div>
                            <div className={styles.uploadButton}>
                                <p className={classnames(["is-size-6", styles.fieldLabelSpan])} >oder</p>
                                <button className="button is-primary is-rounded" onClick={() => { inputField.current.click(); }}>Datei auswählen</button>
                            </div>
                        </>}

                    </div>
                </div>
            </div>
            <button className="modal-close is-large" aria-label="close"></button>
        </div>
        </Portal>

  </div>
}